import type { ReactNode } from 'react';
import { Box } from '../Box';
import { Text } from '../Text';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  // Set false where the screen already announces the empty result through its own live region:
  // two polite regions for one fact is the announcement being doubled or lost, not reinforced.
  announce?: boolean;
}

// Cross-cutting empty state — ADR 0004 step 6 (custom composition).
// Built entirely on façades (Box, Text) — no direct @mui/material import.
// TODO: i18n — copy en ES hasta Fase 1.
export function EmptyState({
  title,
  description,
  icon,
  action,
  announce = true,
}: EmptyStateProps) {
  return (
    <Box
      // Absent, never role={undefined}: exactOptionalPropertyTypes rejects the latter.
      {...(announce ? { role: 'status' as const } : {})}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        py: 6,
        textAlign: 'center',
      }}
    >
      {icon}
      {/* Muted, not secondary/disabled: an empty state tells the reader what to do next, so its
          words must clear AA (text.secondary is 2.37:1; muted is ≈4.6:1 on white). */}
      <Text variant="body1" color="muted">
        {title}
      </Text>
      {description && (
        <Text variant="body2" color="muted">
          {description}
        </Text>
      )}
      {action}
    </Box>
  );
}
